/**
 * WorkspacePhasePermissionsDialog - Modal de configuração de permissões por fase do workspace
 *
 * Wrapper de Dialog que reaproveita o componente WorkspacePhasePermissions
 * (zero duplicação de lógica). Substitui o antigo posicionamento como aba
 * dentro do collapsible do workspace.
 *
 * Layout:
 *  - Desktop: max-w-6xl, ~90dvh, scroll interno.
 *  - Mobile: tela cheia (w-screen h-[100dvh]), conforme padrão de modais responsivos.
 *
 * @version 1.0.0
 * RELEASE: 2026-04-23 v1.0.0 - Criação inicial (refator UI: Tab → Modal)
 */

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Shield } from 'lucide-react';
import { WorkspacePhasePermissions } from './WorkspacePhasePermissions';

interface WorkspacePhasePermissionsDialogProps {
  /** Workspace selecionado (ou null para fechar o modal) */
  workspace: { id: string; name: string; phase_permissions_enabled?: boolean } | null;
  /** Controle de abertura */
  open: boolean;
  /** Callback ao fechar */
  onOpenChange: (open: boolean) => void;
}

export function WorkspacePhasePermissionsDialog({
  workspace,
  open,
  onOpenChange,
}: WorkspacePhasePermissionsDialogProps) {
  if (!workspace) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl w-screen h-[100dvh] sm:w-[95vw] sm:h-[90dvh] flex flex-col p-0 gap-0 sm:rounded-lg">
        {/* Header fixo */}
        <DialogHeader className="px-6 py-4 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Shield className="h-5 w-5 text-primary" />
            Permissões por Fase — {workspace.name}
          </DialogTitle>
          <DialogDescription>
            Configure quais ações cada perfil pode executar em cada fase dos pipelines deste workspace.
          </DialogDescription>
        </DialogHeader>

        {/* Body com scroll */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <WorkspacePhasePermissions
            workspaceId={workspace.id}
            initialEnabled={workspace.phase_permissions_enabled ?? false}
          />
        </div>

        {/* Footer fixo */}
        <DialogFooter className="px-6 py-3 border-t shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
